import { generateUUID } from "./uuid";
import {
  GATEWAY_CLIENT_MODES,
  GATEWAY_CLIENT_NAMES,
  type GatewayClientMode,
  type GatewayClientName,
  type GatewayEventFrame,
  type GatewayResponseFrame,
  type GatewayHelloOk,
  buildDeviceAuthPayload,
} from "./types";
import { loadOrCreateDeviceIdentity, signDevicePayload } from "./device-identity";
import { clearDeviceAuthToken, loadDeviceAuthToken, storeDeviceAuthToken } from "./device-auth";

type Pending = {
  id: string;
  method: string;
  frame: string;
  retryable: boolean;
  retryCount: number;
  resolve: (value: unknown) => void;
  reject: (err: unknown) => void;
};

export type GatewayBrowserClientOptions = {
  url: string;
  token?: string;
  password?: string;
  clerkJwt?: string;
  resolveClerkJwt?: () => Promise<string | undefined> | string | undefined;
  orgId?: string;
  clientName?: GatewayClientName;
  clientVersion?: string;
  platform?: string;
  mode?: GatewayClientMode;
  instanceId?: string;
  onHello?: (hello: GatewayHelloOk) => void;
  onEvent?: (evt: GatewayEventFrame) => void;
  onClose?: (info: { code: number; reason: string }) => void;
  onGap?: (info: { expected: number; received: number }) => void;
};

const CONNECT_FAILED_CLOSE_CODE = 4008;
const ORG_STORAGE_KEY = "command.current-org";
const MAX_RETRYABLE_ATTEMPTS = 3;
const MAX_RETRYABLE_QUEUE_DEPTH = 200;
const RETRYABLE_METHODS = new Set([
  "chat.send",
  "chat.abort",
  "chat.cancel",
  "chat.run.status",
  "chat.run.replay",
  "chat.operation.get",
  "chat.resume",
  "agent.identity.get",
]);

export class GatewayBrowserClient {
  private ws: WebSocket | null = null;
  private pending = new Map<string, Pending>();
  private retryableQueue: string[] = [];
  private retryableQueueSet = new Set<string>();
  private closed = false;
  private lastSeq: number | null = null;
  private connectNonce: string | null = null;
  private connectSent = false;
  private connectTimer: number | null = null;
  private backoffMs = 800;

  constructor(private opts: GatewayBrowserClientOptions) {}

  start() {
    this.closed = false;
    this.connect();
  }

  stop() {
    this.closed = true;
    this.ws?.close();
    this.ws = null;
    this.flushPending(new Error("gateway client stopped"), { preserveRetryable: false });
    this.retryableQueue = [];
    this.retryableQueueSet.clear();
  }

  get connected() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  private connect() {
    if (this.closed) {
      return;
    }
    this.ws = new WebSocket(this.opts.url);
    this.ws.addEventListener("open", () => this.queueConnect());
    this.ws.addEventListener("message", (ev) => this.handleMessage(String(ev.data ?? "")));
    this.ws.addEventListener("close", (ev) => {
      const reason = String(ev.reason ?? "");
      this.ws = null;
      this.flushPending(new Error(`gateway closed (${ev.code}): ${reason}`), {
        preserveRetryable: true,
      });
      this.opts.onClose?.({ code: ev.code, reason });
      this.scheduleReconnect();
    });
    this.ws.addEventListener("error", () => {
      // ignored; close handler will fire
    });
  }

  private scheduleReconnect() {
    if (this.closed) {
      return;
    }
    const delay = this.backoffMs;
    this.backoffMs = Math.min(this.backoffMs * 1.7, 15_000);
    window.setTimeout(() => this.connect(), delay);
  }

  private flushPending(err: Error, opts?: { preserveRetryable?: boolean }) {
    const preserveRetryable = opts?.preserveRetryable === true && !this.closed;
    const keepIds = new Set<string>();
    for (const [id, p] of this.pending) {
      if (preserveRetryable && p.retryable && p.retryCount < MAX_RETRYABLE_ATTEMPTS) {
        p.retryCount += 1;
        this.enqueueRetryable(id);
        keepIds.add(id);
        continue;
      }
      p.reject(err);
    }
    if (preserveRetryable) {
      for (const id of this.pending.keys()) {
        if (!keepIds.has(id)) {
          this.pending.delete(id);
          this.retryableQueueSet.delete(id);
        }
      }
      this.retryableQueue = this.retryableQueue.filter((id) => keepIds.has(id));
      return;
    }
    this.pending.clear();
  }

  private async sendConnect() {
    if (this.connectSent) {
      return;
    }
    this.connectSent = true;
    if (this.connectTimer !== null) {
      window.clearTimeout(this.connectTimer);
      this.connectTimer = null;
    }

    const isSecureContext = typeof crypto !== "undefined" && !!crypto.subtle;

    const scopes = ["operator.admin", "operator.approvals", "operator.pairing"];
    const role = "operator";
    let deviceIdentity: Awaited<ReturnType<typeof loadOrCreateDeviceIdentity>> | null = null;
    let canFallbackToShared = false;
    let authToken = this.opts.token;
    let clerkJwt = this.opts.clerkJwt?.trim() || undefined;
    let orgId: string | undefined;

    if (isSecureContext) {
      deviceIdentity = await loadOrCreateDeviceIdentity();
      const storedToken = loadDeviceAuthToken({
        deviceId: deviceIdentity.deviceId,
        role,
      })?.token;
      authToken = storedToken ?? this.opts.token;
      canFallbackToShared = Boolean(storedToken && this.opts.token);
    }
    if (this.opts.resolveClerkJwt) {
      // Never fall back to an older human credential after a failed fresh lookup.
      clerkJwt = undefined;
      try {
        const resolved = await this.opts.resolveClerkJwt();
        if (typeof resolved === "string" && resolved.trim()) clerkJwt = resolved.trim();
      } catch {
        /* Server returns the generic authentication refusal. */
      }
    }
    const explicitOrgId = this.opts.orgId?.trim();
    if (explicitOrgId) {
      orgId = explicitOrgId;
    }
    try {
      if (!orgId) {
        const stored = localStorage.getItem(ORG_STORAGE_KEY) ?? "";
        const trimmed = stored.trim();
        if (trimmed) {
          orgId = trimmed;
        }
      }
    } catch {
      // Ignore: localStorage may be unavailable in some embedded contexts.
    }
    const auth =
      authToken || this.opts.password || clerkJwt
        ? {
            token: authToken,
            password: this.opts.password,
            clerkJwt,
            orgId,
          }
        : undefined;

    let device:
      | {
          id: string;
          publicKey: string;
          signature: string;
          signedAt: number;
          nonce: string | undefined;
        }
      | undefined;

    if (isSecureContext && deviceIdentity) {
      const signedAtMs = Date.now();
      const nonce = this.connectNonce ?? undefined;
      const payload = buildDeviceAuthPayload({
        deviceId: deviceIdentity.deviceId,
        clientId: this.opts.clientName ?? GATEWAY_CLIENT_NAMES.CONTROL_UI,
        clientMode: this.opts.mode ?? GATEWAY_CLIENT_MODES.WEBCHAT,
        role,
        scopes,
        signedAtMs,
        token: authToken ?? null,
        nonce,
      });
      const signature = await signDevicePayload(deviceIdentity.privateKey, payload);
      device = {
        id: deviceIdentity.deviceId,
        publicKey: deviceIdentity.publicKey,
        signature,
        signedAt: signedAtMs,
        nonce,
      };
    }
    const params = {
      minProtocol: 3,
      maxProtocol: 3,
      client: {
        id: this.opts.clientName ?? GATEWAY_CLIENT_NAMES.CONTROL_UI,
        version: this.opts.clientVersion ?? "dev",
        platform: this.opts.platform ?? navigator.platform ?? "web",
        mode: this.opts.mode ?? GATEWAY_CLIENT_MODES.WEBCHAT,
        instanceId: this.opts.instanceId,
      },
      role,
      scopes,
      device,
      caps: [],
      auth,
      userAgent: navigator.userAgent,
      locale: navigator.language,
    };

    void this.request<GatewayHelloOk>("connect", params)
      .then((hello) => {
        if (hello?.auth?.deviceToken && deviceIdentity) {
          storeDeviceAuthToken({
            deviceId: deviceIdentity.deviceId,
            role: hello.auth.role ?? role,
            token: hello.auth.deviceToken,
            scopes: hello.auth.scopes ?? [],
          });
        }
        this.backoffMs = 800;
        this.opts.onHello?.(hello);
        this.flushRetryableQueue();
      })
      .catch(() => {
        if (canFallbackToShared && deviceIdentity) {
          clearDeviceAuthToken({ deviceId: deviceIdentity.deviceId, role });
        }
        this.ws?.close(CONNECT_FAILED_CLOSE_CODE, "connect failed");
      });
  }

  private handleMessage(raw: string) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }

    const frame = parsed as { type?: unknown };
    if (frame.type === "event") {
      const evt = parsed as GatewayEventFrame;
      if (evt.event === "connect.challenge") {
        const payload = evt.payload as { nonce?: unknown } | undefined;
        const nonce = payload && typeof payload.nonce === "string" ? payload.nonce : null;
        if (nonce) {
          this.connectNonce = nonce;
          void this.sendConnect();
        }
        return;
      }
      const seq = typeof evt.seq === "number" ? evt.seq : null;
      if (seq !== null) {
        if (this.lastSeq !== null && seq > this.lastSeq + 1) {
          this.opts.onGap?.({ expected: this.lastSeq + 1, received: seq });
        }
        this.lastSeq = seq;
      }
      try {
        this.opts.onEvent?.(evt);
      } catch (err) {
        console.error("[gateway] event handler error:", err);
      }
      return;
    }

    if (frame.type === "res") {
      const res = parsed as GatewayResponseFrame;
      const pending = this.pending.get(res.id);
      if (!pending) {
        return;
      }
      this.pending.delete(res.id);
      if (res.ok) {
        pending.resolve(res.payload);
      } else {
        pending.reject(new Error(res.error?.message ?? "request failed"));
      }
      return;
    }
  }

  request<T = unknown>(method: string, params?: unknown): Promise<T> {
    const id = generateUUID();
    const frame = { type: "req", id, method, params };
    const frameJson = JSON.stringify(frame);
    const retryable = this.isRetryableRequest(method, params);

    const promise = new Promise<T>((resolve, reject) => {
      this.pending.set(id, {
        id,
        method,
        frame: frameJson,
        retryable,
        retryCount: 0,
        resolve: (v) => resolve(v as T),
        reject,
      });

      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        if (!retryable) {
          this.pending.delete(id);
          reject(new Error("gateway not connected"));
          return;
        }
        this.enqueueRetryable(id);
        if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
          this.connect();
        }
        return;
      }

      try {
        this.ws.send(frameJson);
      } catch (err) {
        if (!retryable) {
          this.pending.delete(id);
          reject(err instanceof Error ? err : new Error(String(err)));
          return;
        }
        this.enqueueRetryable(id);
        // Trigger close/reconnect flow without creating a second concurrent socket.
        this.ws?.close();
      }
    });
    return promise;
  }

  private isRetryableRequest(method: string, params?: unknown): boolean {
    if (method === "connect") {
      return false;
    }
    if (!RETRYABLE_METHODS.has(method)) {
      return false;
    }
    if (method !== "chat.send") {
      return true;
    }
    if (!params || typeof params !== "object") {
      return false;
    }
    const runId = (params as { idempotencyKey?: unknown }).idempotencyKey;
    return typeof runId === "string" && runId.trim().length > 0;
  }

  private enqueueRetryable(id: string) {
    if (this.retryableQueueSet.has(id)) {
      return;
    }
    if (this.retryableQueue.length >= MAX_RETRYABLE_QUEUE_DEPTH) {
      const droppedId = this.retryableQueue.shift();
      if (droppedId) {
        this.retryableQueueSet.delete(droppedId);
        const pending = this.pending.get(droppedId);
        if (pending) {
          this.pending.delete(droppedId);
          pending.reject(new Error("gateway retry queue overflow"));
        }
      }
    }
    this.retryableQueue.push(id);
    this.retryableQueueSet.add(id);
  }

  private flushRetryableQueue() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }
    while (this.retryableQueue.length > 0) {
      const id = this.retryableQueue[0];
      const pending = this.pending.get(id);
      if (!pending) {
        this.retryableQueue.shift();
        this.retryableQueueSet.delete(id);
        continue;
      }
      try {
        this.ws.send(pending.frame);
        this.retryableQueue.shift();
        this.retryableQueueSet.delete(id);
      } catch {
        break;
      }
    }
  }

  private queueConnect() {
    this.connectNonce = null;
    this.connectSent = false;
    if (this.connectTimer !== null) {
      window.clearTimeout(this.connectTimer);
    }
    this.connectTimer = window.setTimeout(() => {
      void this.sendConnect();
    }, 750);
  }
}
